/**
 * Generic ``<DataTable<T>>`` primitive (refreshed).
 *
 * Now wraps the shadcn ``<Table>`` primitives, gets a real skeleton
 * loading state, and respects mobile via horizontal scroll. Row click
 * is optional — pass ``onRowClick`` to make the row a keyboard-focusable
 * navigation/selection target.
 */
import { type ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Optional className applied to both the <th> and <td> for layout. */
  className?: string;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<DataTableColumn<T>>;
  rows: ReadonlyArray<T>;
  loading?: boolean;
  /** Rows are the previous result, kept on screen while the new one loads. */
  stale?: boolean;
  emptyState?: ReactNode;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Marker for the currently-selected row (e.g. open in a detail Sheet). */
  selectedKey?: string;
  skeletonRows?: number;
  /** Extra classes for a row, e.g. a tint on rows that need attention. */
  rowClassName?: (row: T) => string | undefined;
}

export function DataTable<T>({
  columns,
  rows,
  loading = false,
  stale = false,
  emptyState,
  rowKey,
  onRowClick,
  selectedKey,
  skeletonRows = 6,
  rowClassName,
}: DataTableProps<T>) {
  if (loading || (stale && rows.length === 0)) {
    return (
      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                {columns.map((col) => (
                  <TableCell key={col.key} className={col.className}>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (rows.length === 0 && emptyState !== undefined) {
    return <>{emptyState}</>;
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border bg-card shadow-sm transition-opacity",
        stale && "opacity-60",
      )}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.className}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const key = rowKey(row);
            const clickable = onRowClick !== undefined;
            const isSelected = selectedKey === key;
            return (
              <TableRow
                key={key}
                data-state={isSelected ? "selected" : undefined}
                className={cn(clickable && "group cursor-pointer", rowClassName?.(row))}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={clickable ? 0 : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} className={col.className}>
                    {col.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
